import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Zap, CheckCircle, Flame } from 'lucide-react';

function useCountdown(targetDateStr: string | null) {
  const [units, setUnits] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (!targetDateStr) return;
    const target = new Date(targetDateStr).getTime();

    function tick() {
      const diff = Math.max(0, target - Date.now());
      setUnits({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    }

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDateStr]);

  return units;
}

const pad = (n: number) => String(n).padStart(2, '0');

interface PendingActionsBannerProps {
  unbettedSeriesCount: number;
  unpickedSideBetsCount: number;
  groupId: string;
  /** When true, show calm pre-season version with countdown */
  prePlayoff?: boolean;
  /** Target date for countdown (e.g. betting deadline) */
  countdownTarget?: string;
}

interface ActionItemProps {
  icon: React.ElementType;
  label: string;
  count?: number;
  gold?: boolean;
  to?: string;
  onClick?: () => void;
}

function ActionItem({ icon: Icon, label, count, gold, to, onClick }: ActionItemProps) {
  const iconColor = gold ? '#C4A467' : 'hsl(24 95% 53%)';
  const iconBg = gold ? 'rgba(196, 164, 103, 0.1)' : 'hsl(24 95% 53% / 0.1)';
  const iconBorder = gold ? 'rgba(196, 164, 103, 0.3)' : 'hsl(24 95% 53% / 0.3)';

  const content = (
    <div className="flex items-center gap-2.5 min-w-0">
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
        style={{ backgroundColor: iconBg, border: `1px solid ${iconBorder}` }}
      >
        <Icon className="w-4 h-4" style={{ color: iconColor }} />
      </div>
      <div className="min-w-0">
        <p className="text-base font-semibold text-white truncate">{label}</p>
        {count !== undefined && (
          <p className="text-base text-white/70">{count} ממתינים</p>
        )}
      </div>
    </div>
  );

  const cls = 'flex-1 min-w-0 rounded-xl bg-card/60 border border-border/50 px-3 py-2.5 cursor-pointer hover:bg-card transition-colors';

  if (to) {
    return <Link to={to} className={cls}>{content}</Link>;
  }
  return <button type="button" onClick={onClick} className={`${cls} text-right`}>{content}</button>;
}

export function PendingActionsBanner({
  unbettedSeriesCount,
  unpickedSideBetsCount,
  groupId,
  prePlayoff,
  countdownTarget,
}: PendingActionsBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  const total = unbettedSeriesCount + unpickedSideBetsCount;

  // Auto-dismiss the "all done" message after 5 seconds
  useEffect(() => {
    if (total === 0 && !dismissed && !prePlayoff) {
      const timer = setTimeout(() => setDismissed(true), 5000);
      return () => clearTimeout(timer);
    }
  }, [total, dismissed, prePlayoff]);

  // ── Pre-playoff: calm hype banner with countdown ──────────────────────────
  const countdown = useCountdown(prePlayoff && countdownTarget ? countdownTarget : null);

  if (prePlayoff) {
    const timerUnits = [
      { value: pad(countdown.days), label: 'ימים' },
      { value: pad(countdown.hours), label: 'שעות' },
      { value: pad(countdown.minutes), label: 'דקות' },
      { value: pad(countdown.seconds), label: 'שניות' },
    ];

    return (
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-orange-500/25 bg-orange-500/[0.07] p-4"
        dir="rtl"
      >
        <div className="flex items-center gap-2 mb-1">
          <Flame className="w-5 h-5 text-orange-400" />
          <p className="text-white font-bold text-lg tracking-tight">
            הפלייאוף מתקרב!
          </p>
        </div>
        <p className="text-sm text-white/60 mb-3">
          ההימורים ייפתחו בקרוב...
        </p>
        {countdownTarget && (
          <>
            <div className="flex items-center justify-center gap-3" dir="ltr">
              {timerUnits.map(({ value, label }) => (
                <div key={label} className="flex flex-col items-center leading-none bg-black/20 rounded-xl px-3 py-2 min-w-[56px]">
                  <span className="text-white font-extrabold text-2xl tabular-nums tracking-tight">
                    {value}
                  </span>
                  <span className="text-white/50 text-[10px] mt-1 font-medium">{label}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-white/40 text-center mt-2">
              ההימורים על סדרות הפליי-אין (מקומות 7-8) ייפתחו ב-18 באפריל בשעה 08:00
            </p>
          </>
        )}
      </motion.div>
    );
  }

  if (total === 0 && dismissed) return null;

  return (
    <AnimatePresence>
      {total === 0 ? (
        <motion.div
          key="done"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          className="bg-green-500/10 border border-green-500/30 rounded-2xl p-4 flex items-center justify-center gap-2"
          dir="rtl"
        >
          <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
          <span className="text-sm font-medium text-green-400">
            סיימת הכל! עכשיו רק לחכות לתוצאות
          </span>
        </motion.div>
      ) : (
        <motion.div
          key="pending"
          id="pending-section"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          className="rounded-2xl border border-orange-500/30 bg-orange-500/10 p-4"
          dir="rtl"
        >
          <p className="text-orange-400 font-bold text-lg mb-3 tracking-tight">מהר להשלים הימורים</p>

          <div className="flex gap-2">
            {unbettedSeriesCount > 0 && (
              <ActionItem
                icon={Target}
                label="סדרות"
                count={unbettedSeriesCount}
                onClick={() =>
                  document.getElementById('series-section')?.scrollIntoView({ behavior: 'smooth' })
                }
              />
            )}
            {unpickedSideBetsCount > 0 && (
              <ActionItem
                icon={Zap}
                label="הימורי צד"
                count={unpickedSideBetsCount}
                gold
                to={`/group/${groupId}/side-bets`}
              />
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
